import { BackendUser, UserPreferences } from '../types/types';
import api, { createResource, fetchData, updateResource } from './api';
import { logger } from './logger';

export interface UserCreateData {
  firebase_uid: string;
  email: string;
  display_name?: string | null;
  user_type: 'vendor' | 'consumer';
}

export interface VendorUserData extends BackendUser {
  vendor_details?: {
    id: number;
    name: string;
    category: string;
  } | null;
}

// Get a user by Firebase UID
export const getUserByFirebaseUid = async (
  firebaseUid: string,
  includeVendorDetails = false
): Promise<BackendUser | VendorUserData> => {
  try {
    const params = includeVendorDetails ? { with_vendor_details: 'true' } : undefined;
    return await fetchData<BackendUser | VendorUserData>(`/users/firebase/${firebaseUid}/`, params);
  } catch (error) {
    logger.error(`Error fetching user with Firebase UID ${firebaseUid}:`, error);
    throw error;
  }
};

// Register a new user
export const registerUser = async (userData: UserCreateData): Promise<BackendUser> => {
  try {
    return await createResource<BackendUser, UserCreateData>('/register/', userData);
  } catch (error) {
    logger.error('Error registering user:', error);
    throw error;
  }
};

// Update a user
export const updateUser = async (
  userId: number,
  userData: Partial<BackendUser>
): Promise<BackendUser> => {
  try {
    return await updateResource<BackendUser>(`/users/${userId}/`, userData);
  } catch (error) {
    logger.error(`Error updating user ${userId}:`, error);
    throw error;
  }
};

// Get current user profile (uses the current Firebase UID from auth context)
export const getCurrentUserProfile = async (
  includeVendorDetails = false
): Promise<BackendUser | VendorUserData | null> => {
  try {
    const currentUser = api.defaults.headers.common['Authorization'];
    if (!currentUser) {
      return null; // Not authenticated
    }

    // Get Firebase user ID from your auth context
    const firebaseUser = await api.get('/auth/current-user/');
    if (!firebaseUser.data?.uid) {
      return null;
    }

    return await getUserByFirebaseUid(firebaseUser.data.uid, includeVendorDetails);
  } catch (error) {
    logger.error('Error fetching current user profile:', error);
    return null;
  }
};

export const updateUserPreferences = async (
  userId: number,
  preferences: Partial<UserPreferences>
): Promise<BackendUser> => {
  try {
    return await updateResource<BackendUser>(`/users/${userId}/`, {
      user_preferences: preferences,
    });
  } catch (error) {
    logger.error(`Error updating user preferences for user ${userId}:`, error);
    throw error;
  }
};

// Update user profile (display name, preferences)
export const updateUserProfile = async (
  userId: number,
  updates: { display_name?: string; user_preferences?: Partial<UserPreferences> }
): Promise<BackendUser> => {
  try {
    return await updateResource<BackendUser>(`/users/${userId}/`, updates);
  } catch (error) {
    logger.error(`Error updating user profile for user ${userId}:`, error);
    throw error;
  }
};

export default {
  getUserByFirebaseUid,
  registerUser,
  updateUser,
  getCurrentUserProfile,
};
